import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Returns an ordered signature of the gallery's media, based on each
   * slide's data-media-id.
   *
   * Reads from a single context (the slideshow if present, otherwise the
   * gallery root) and de-duplicates IDs, because the same media is rendered
   * multiple times across the carousel, the desktop grid, and the zoom dialog.
   * Counting all of them produces an unstable signature that changes between
   * variants even when the actual image set is identical.
   *
   * Identical media sets (e.g. size changes on a shared-image product) produce
   * identical signatures; different media sets (e.g. colourways with their own
   * featured image) do not.
   *
   * @param {Element} galleryEl - The media-gallery element to read from.
   * @returns {string}
   */
  #getMediaSignature(galleryEl) {
    // Prefer the slideshow context so we read one consistent ordering.
    const scope = galleryEl.querySelector('slideshow-component') ?? galleryEl;

    const ids = Array.from(scope.querySelectorAll('[data-media-id]'))
      .map((el) => el.getAttribute('data-media-id'))
      .filter(Boolean);

    // De-duplicate while preserving first-seen order.
    return Array.from(new Set(ids)).join(',');
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * If the new variant's media set is identical to the current one (same images
   * in the same order), the active slide index is preserved so switching sizes
   * feels fluid and never jumps back to the first image. If the media set
   * differs (a true colourway change), the gallery is left to reset to the new
   * variant's image as normal.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Capture state from the gallery that's about to be replaced.
    const previousSignature = this.#getMediaSignature(this);
    const previousIndex = this.slideshow?.current ?? 0;

    // Compare against the incoming gallery's media set.
    const newSignature = this.#getMediaSignature(newMediaGallery);
    const sameMediaSet = previousSignature !== '' && previousSignature === newSignature;

    // If the images are unchanged, tell the new slideshow to mount on the
    // slide the customer was already viewing instead of resetting to 0.
    if (sameMediaSet && previousIndex > 0) {
      const newSlideshow = newMediaGallery.querySelector('slideshow-component');
      if (newSlideshow) {
        newSlideshow.setAttribute('initial-slide', String(previousIndex));
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}